<template>
  <div
    class="modern-container"
    role="main"
    aria-label="Liste des réclamations livreur"
  >
    <div class="tickets-header">
      <div class="header-content">
        <h1 class="simple-title">
          Réclamations Livreur
        </h1>
      </div>
    </div>

    <div class="modern-card">
      <div class="card-header">
        <div
          class="stats-row"
          aria-label="Statistiques des réclamations livreur"
        >
          <div class="stat-item">
            <div class="stat-icon-wrapper text-primary">
              <feather-icon icon="FileTextIcon" size="24" />
            </div>
            <span class="stat-number" aria-live="polite">{{ (stats && stats.total) != null ? stats.total : (pagination.count || tickets.length) }}</span>
            <span class="stat-label">Total</span>
          </div>
          <div
            v-if="stats"
            class="stat-item"
          >
            <div class="stat-icon-wrapper text-info">
              <feather-icon icon="BellIcon" size="24" />
            </div>
            <span class="stat-number">{{ stats.new || 0 }}</span>
            <span class="stat-label">Nouveaux</span>
          </div>
          <div
            v-if="stats"
            class="stat-item"
          >
            <div class="stat-icon-wrapper text-warning">
              <feather-icon icon="RefreshCwIcon" size="24" />
            </div>
            <span class="stat-number">{{ stats.in_progress || 0 }}</span>
            <span class="stat-label">En cours</span>
          </div>
          <div
            v-if="stats"
            class="stat-item"
          >
            <div class="stat-icon-wrapper text-success">
              <feather-icon icon="CheckCircleIcon" size="24" />
            </div>
            <span class="stat-number">{{ stats.resolved || 0 }}</span>
            <span class="stat-label">Résolus</span>
          </div>
        </div>

        <div
          class="filter-bar mt-4 mb-2 px-0 px-md-3 py-2 d-flex flex-wrap align-items-center justify-content-between"
          style="background: #f8f9fa; border-radius: 12px; box-shadow: 0 1px 4px rgba(0,0,0,0.04);"
        >
          <div class="d-flex flex-wrap align-items-center gap-3">
            <div class="filter-group d-flex align-items-center mr-3 mb-2 mb-md-0">
              <i class="fas fa-search text-muted mr-2" />
              <input
                v-model="search"
                type="text"
                class="form-control filter-input"
                placeholder="Rechercher (commande, livreur, motif)"
                aria-label="Recherche"
                style="min-width: 220px; border-radius: 8px; border: 1px solid #e2e8f0; box-shadow: none;"
                @keyup.enter="applyFilters"
              >
            </div>

            <div class="filter-group d-flex align-items-center mr-3 mb-2 mb-md-0">
              <i class="fas fa-filter text-muted mr-2" />
              <select
                v-model="statusFilter"
                class="form-control filter-select"
                aria-label="Filtrer par statut"
                style="min-width: 170px; border-radius: 8px; border: 1px solid #e2e8f0; box-shadow: none;"
                @change="applyFilters"
              >
                <option value="">
                  Tous les statuts
                </option>
                <option value="new">
                  Nouveau
                </option>
                <option value="in_progress">
                  En cours
                </option>
                <option value="resolved">
                  Résolu
                </option>
                <option value="closed">
                  Fermé
                </option>
                <option value="escalated">
                  Escaladé
                </option>
              </select>
            </div>

            <div class="filter-group d-flex align-items-center mr-3 mb-2 mb-md-0">
              <i class="fas fa-calendar text-muted mr-2" />
              <select
                v-model="dateRangeFilter"
                class="form-control filter-select"
                aria-label="Filtrer par période"
                style="min-width: 170px; border-radius: 8px; border: 1px solid #e2e8f0; box-shadow: none;"
                @change="applyFilters"
              >
                <option value="">
                  Toutes les dates
                </option>
                <option value="today">
                  Aujourd'hui
                </option>
                <option value="yesterday">
                  Hier
                </option>
                <option value="this_week">
                  Cette semaine
                </option>
                <option value="last_week">
                  Semaine dernière
                </option>
                <option value="this_month">
                  Ce mois
                </option>
                <option value="last_month">
                  Mois dernier
                </option>
                <option value="last_7_days">
                  7 derniers jours
                </option>
                <option value="last_30_days">
                  30 derniers jours
                </option>
              </select>
            </div>

            <div class="filter-group d-flex align-items-center mr-3 mb-2 mb-md-0">
              <i class="fas fa-user text-muted mr-2" />
              <select
                v-model="driverId"
                class="form-control filter-select"
                aria-label="Filtrer par livreur"
                style="min-width: 220px; border-radius: 8px; border: 1px solid #e2e8f0; box-shadow: none;"
                @change="applyFilters"
              >
                <option value="">
                  Tous les livreurs
                </option>
                <option
                  v-for="d in drivers"
                  :key="d.id"
                  :value="d.id"
                >
                  {{ d.full_name || d.username }}
                </option>
              </select>
            </div>

            <div class="filter-group d-flex align-items-center mr-3 mb-2 mb-md-0">
              <i class="fas fa-phone text-muted mr-2" />
              <input
                v-model.number="minContacts"
                type="number"
                min="0"
                class="form-control filter-input mr-2"
                placeholder="Min contacts"
                aria-label="Min tentatives"
                style="width: 130px; border-radius: 8px; border: 1px solid #e2e8f0; box-shadow: none;"
                @keyup.enter="applyFilters"
              >
              <input
                v-model.number="maxContacts"
                type="number"
                min="0"
                class="form-control filter-input"
                placeholder="Max contacts"
                aria-label="Max tentatives"
                style="width: 130px; border-radius: 8px; border: 1px solid #e2e8f0; box-shadow: none;"
                @keyup.enter="applyFilters"
              >
            </div>

            <div class="filter-group d-flex align-items-center mr-3 mb-2 mb-md-0">
              <div class="form-check mr-3">
                <input
                  id="activeTickets"
                  v-model="activeTickets"
                  class="form-check-input"
                  type="checkbox"
                  @change="applyFilters"
                >
                <label
                  class="form-check-label"
                  for="activeTickets"
                >Actifs</label>
              </div>
              <div class="form-check">
                <input
                  id="canBeResolved"
                  v-model="canBeResolved"
                  class="form-check-input"
                  type="checkbox"
                  @change="applyFilters"
                >
                <label
                  class="form-check-label"
                  for="canBeResolved"
                >Résolvables</label>
              </div>
            </div>

            <button
              class="btn btn-outline-secondary ml-2"
              aria-label="Réinitialiser les filtres"
              style="border-radius: 8px;"
              @click="resetFilters"
            >
              <i class="fas fa-times mr-1" /> Réinitialiser
            </button>
          </div>
        </div>

        <div
          v-if="pagination.count !== undefined"
          class="debug-info mt-2 text-muted small"
        >
          <i class="fas fa-info-circle mr-1" />
          Pagination: {{ pagination.current_page || currentPage }}/{{ pagination.total_pages }} pages,
          {{ pagination.page_size }} par page,
          {{ pagination.count }} total
        </div>
      </div>

      <div class="modern-table-container">
        <template v-if="loading">
          <div class="loading-state">
            <b-spinner
              variant="danger"
              label="Chargement des tickets..."
              style="width: 3rem; height: 3rem;"
              class="mb-3"
            />
            <span>Chargement des tickets...</span>
          </div>
        </template>
        <template v-else-if="tickets.length === 0">
          <div class="text-center py-5">
            <div
              class="no-results-card shadow-sm enhanced-empty-state"
              style="background: #fff; border-radius: 20px; padding: 2.5rem 2rem; max-width: 520px; margin: 0 auto; box-shadow: 0 8px 32px rgba(156,163,175,0.10);"
            >
              <div class="mb-4">
                <img
                  src="/public/img/icons/empty-support.svg"
                  alt="Aucune réclamation"
                  style="height: 110px; opacity: 0.85;"
                  onerror="this.style.display='none'"
                >
              </div>
              <h3
                class="text-muted mb-2 font-weight-bold"
                style="font-size: 1.5rem;"
              >
                Aucune réclamation trouvée
              </h3>
              <p
                class="text-muted mb-4"
                style="font-size: 1.08rem;"
              >
                <span v-if="search || statusFilter || dateRangeFilter || driverId">
                  <span style="font-weight: 500;">Aucun ticket ne correspond à vos critères.</span><br>
                  Essayez d'élargir votre recherche ou de réinitialiser les filtres pour voir plus de résultats.
                </span>
                <span v-else>
                  <span style="font-weight: 500;">Il n'y a actuellement aucune réclamation livreur.</span><br>
                  Les tickets apparaîtront ici lorsqu'ils seront créés.
                </span>
              </p>
              <div v-if="search || statusFilter || dateRangeFilter || driverId">
                <b-button
                  variant="outline-primary"
                  size="lg"
                  class="mt-1 px-4"
                  @click="resetFilters"
                >
                  <i class="fas fa-sync-alt mr-1" /> Réinitialiser les filtres
                </b-button>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <b-table
            :items="tickets"
            :fields="tableFields"
            responsive="sm"
            class="modern-table driver-ticket-table"
            hover
            borderless
            aria-label="Tableau des réclamations livreur"
          >
            <template #cell(select)="{ item }">
              <div class="text-center">
                <input
                  :id="'sel-'+item.id"
                  type="checkbox"
                  :checked="isSelected(item.id)"
                  aria-label="Sélectionner le ticket"
                  @change="toggleSelect(item.id)"
                >
              </div>
            </template>
            <template #cell(id)="{ item }">
              <span
                v-b-tooltip.hover
                class="order-badge"
                :title="'Ticket #' + item.id"
              >#{{ item.id }}</span>
            </template>
            <template #cell(order)="{ item }">
              <div class="order-cell">
                <span
                  v-b-tooltip.hover
                  class="order-badge"
                  :title="'Commande #' + (item.order && item.order.id ? item.order.id : item.order)"
                >
                  #{{ (item.order && item.order.id) ? item.order.id : item.order }}
                </span>
              </div>
            </template>
            <template #cell(customer)="{ item }">
              <span>{{ (item.order && item.order.name) || '-' }}</span>
            </template>
            <template #cell(phones)="{ item }">
              <span>
                {{ [item.order && item.order.phone1, item.order && item.order.phone2].filter(Boolean).join(' / ') || '-' }}
              </span>
            </template>
            <template #cell(driver)="{ item }">
              <div class="user-cell">
                <span class="user-name">{{ item.driver && (item.driver.full_name || item.driver.username) ? (item.driver.full_name || item.driver.username) : '-' }}</span>
              </div>
            </template>
            <template #cell(supplier)="{ item }">
              <span>{{ (item.order && item.order.supplier && item.order.supplier.username) || '-' }}</span>
            </template>
            <template #cell(zone)="{ item }">
              <span>
                {{ [item.order && item.order.governorate && item.order.governorate.name, item.order && item.order.delegation && item.order.delegation.name].filter(Boolean).join(' / ') || '-' }}
              </span>
            </template>
            <template #cell(amount)="{ item }">
              <span>{{ (item.order && item.order.price_ttc) || '-' }}</span>
            </template>
            <template #cell(reason_type)="{ item }">
              <span>{{ formatReason(item.reason_type) }}</span>
            </template>
            <template #cell(status)="{ item }">
              <div class="status-cell">
                <span
                  :class="['status-pill', getStatusClass(item.status)]"
                  :style="item.status_color ? { backgroundColor: item.status_color, color: '#fff' } : null"
                >
                  <i :class="getStatusIcon(item.status)" />
                  {{ item.status_display || getStatusLabel(item.status) }}
                </span>
                <div
                  v-if="canManageStatuses"
                  class="status-actions"
                >
                  <b-button
                    v-for="action in statusShortcutButtons"
                    :key="`${item.id}-${action.value}`"
                    size="sm"
                    :variant="action.variant"
                    class="status-shortcut-btn"
                    :disabled="isRowBusy(item.id) || !canTransitionTo(item, action.value)"
                    @click="updateTicketStatus(item, action.value)"
                  >
                    <i
                      v-if="isRowBusy(item.id) && activeStatusChange === `${item.id}:${action.value}`"
                      class="fas fa-spinner fa-spin mr-1"
                    />
                    {{ action.label }}
                  </b-button>
                </div>
                <small
                  v-if="canManageStatuses && !hasAnyStatusShortcut(item)"
                  class="text-muted d-block mt-1"
                >
                  Aucun changement possible
                </small>
              </div>
            </template>
            <template #cell(contact_attempts)="{ item }">
              <span>{{ item.contact_attempts != null ? item.contact_attempts : '-' }}</span>
            </template>
            <template #cell(fragile)="{ item }">
              <span>{{ (item.order && item.order.is_fragile) ? 'Oui' : 'Non' }}</span>
            </template>
            <template #cell(resolvable)="{ item }">
              <span>{{ item.can_be_resolved_by_driver ? 'Oui' : 'Non' }}</span>
            </template>
            <template #cell(note)="{ item }">
              <span>{{ item.additional_note || '-' }}</span>
            </template>
            <template #cell(created_at)="{ item }">
              <div class="date-cell">
                <span class="date-text">{{ formatDate(item.created_at) }}</span>
              </div>
            </template>
            <template #cell(updated_at)="{ item }">
              <div class="date-cell">
                <span class="date-text">{{ formatDate(item.updated_at) }}</span>
              </div>
            </template>
            <template #cell(actions)="{ item }">
              <div class="actions-cell">
                <b-button
                  v-b-tooltip.hover
                  size="sm"
                  variant="outline-secondary"
                  class="icon-btn"
                  :aria-label="'Aperçu pour le ticket '+item.id"
                  title="Aperçu (raccourci: .)"
                  @mouseenter="lastHoverId = item.id"
                  @click.stop="openQuickView(item)"
                >
                  <i
                    class="fas fa-columns"
                    aria-hidden="true"
                  />
                </b-button>
              </div>
            </template>
          </b-table>

          <div
            v-if="canManageStatuses && selectedIds.length"
            class="bulk-toolbar d-flex align-items-center justify-content-between px-3 py-2 mt-3"
          >
            <div class="text-muted small">
              <i class="fas fa-check-square mr-2" /> {{ selectedIds.length }} sélectionné(s)
            </div>
            <div class="d-flex align-items-center gap-2">
              <b-button
                size="sm"
                variant="outline-success"
                class="mr-2"
                @click="bulkResolve"
              >
                <i class="fas fa-check mr-1" /> Résoudre
              </b-button>
              <b-button
                size="sm"
                variant="outline-warning"
                class="mr-2"
                @click="bulkEscalate"
              >
                <i class="fas fa-exclamation-triangle mr-1" /> Escalader
              </b-button>
              <b-button
                size="sm"
                variant="outline-dark"
                class="mr-3"
                @click="bulkClose"
              >
                <i class="fas fa-times mr-1" /> Fermer
              </b-button>
              <b-button
                size="sm"
                variant="outline-secondary"
                @click="clearSelection"
              >
                Effacer
              </b-button>
            </div>
          </div>

          <div
            v-if="pagination.count > pagination.page_size"
            class="pagination-container d-flex justify-content-between align-items-center mt-4 px-3"
          >
            <div class="pagination-info">
              <span class="text-muted">
                Affichage de {{ getDisplayRange() }} sur {{ pagination.count }} tickets
              </span>
              <div class="page-size-selector ml-3 d-inline-block">
                <label class="text-muted mr-2">Items par page:</label>
                <select
                  v-model="pageSize"
                  class="form-control d-inline-block"
                  style="width: auto; font-size: 0.875rem;"
                  @change="changePageSize"
                >
                  <option value="10">
                    10
                  </option>
                  <option value="20">
                    20
                  </option>
                  <option value="50">
                    50
                  </option>
                  <option value="100">
                    100
                  </option>
                </select>
              </div>
            </div>
            <b-pagination
              v-model="currentPage"
              :total-rows="pagination.count"
              :per-page="pagination.page_size"
              :limit="5"
              size="md"
              class="mb-0"
              @change="onPageChange"
            />
          </div>
        </template>
      </div>
    </div>
    <DriverTicketQuickView
      :visible="quickViewVisible"
      :ticket="quickViewTicket"
      :loading-id="actionLoadingId"
      :can-manage="canManageStatuses"
      @take="quickViewTicket && takeInProgress(quickViewTicket)"
      @add-contact="quickViewTicket && addContactAttempt(quickViewTicket.id)"
      @resolve="quickViewTicket && resolveTicket(quickViewTicket)"
      @escalate="quickViewTicket && escalateTicket(quickViewTicket)"
      @close-ticket="quickViewTicket && closeTicket(quickViewTicket)"
      @view-detail="quickViewTicket && goToDetail(quickViewTicket)"
      @close="closeQuickView"
    />
  </div>
</template>

<script>
import {
  BButton, BTable, BPagination, BSpinner,
} from 'bootstrap-vue'
import axios from '@/libs/axios'
import storeAuth from '@/store/store'
import DriverTicketQuickView from './DriverTicketQuickView.vue'

export default {
  name: 'DriverTicketList',
  components: {
    BButton, BTable, BPagination, BSpinner, DriverTicketQuickView,
  },
  data() {
    return {
      tickets: [],
      loading: false,
      fields: [
        { key: 'select', label: '', sortable: false },
        { key: 'id', label: 'ID', sortable: true },
        { key: 'order', label: 'Commande', sortable: false },
        { key: 'customer', label: 'Client', sortable: false },
        { key: 'phones', label: 'Téléphones', sortable: false },
        { key: 'driver', label: 'Livreur', sortable: false },
        { key: 'reason_type', label: 'Motif', sortable: false },
        { key: 'status', label: 'Statut', sortable: true },
        { key: 'contact_attempts', label: 'Contacts', sortable: true },
        { key: 'note', label: 'Note', sortable: false },
        { key: 'created_at', label: 'Créé le', sortable: true },
        { key: 'updated_at', label: 'Mis à jour', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
      // filters
      search: '',
      statusFilter: '',
      dateRangeFilter: '',
      driverId: '',
      minContacts: '',
      maxContacts: '',
      activeTickets: false,
      canBeResolved: false,
      drivers: [],
      // pagination
      currentPage: 1,
      pageSize: 20,
      pagination: {
        count: 0,
        next: null,
        previous: null,
        page_size: 20,
        current_page: 1,
        total_pages: 1,
      },
      async fetchDrivers() {
        try {
          const { data } = await axios.get('/api/auth/users/drivers/')
          this.drivers = Array.isArray(data) ? data : (data.results || [])
        } catch (e) {
        // eslint-disable-next-line no-console
          console.error('Error fetching drivers', e)
        }
      },
      stats: null,
      // quick view & inline actions
      quickViewVisible: false,
      quickViewTicket: null,
      actionLoadingId: null,
      activeStatusChange: null,
      // selection and shortcuts
      selectedIds: [],
      lastHoverId: null,
    }
  },
  computed: {
    userRole() {
      return storeAuth.getters.userRole || storeAuth.state.user?.role || ''
    },
    canManageStatuses() {
      return ['admin', 'superadmin', 'customer_support', 'warehouse_operator'].includes(this.userRole)
    },
    tableFields() {
      if (this.canManageStatuses) return this.fields
      return this.fields.filter(field => field.key !== 'select')
    },
    statusShortcutButtons() {
      return [
        { value: 'in_progress', label: 'En cours', variant: 'outline-warning' },
        { value: 'resolved', label: 'Résolu', variant: 'outline-success' },
        { value: 'closed', label: 'Fermé', variant: 'outline-dark' },
      ]
    },
  },
  created() {
    this.applyFilters()
    this.fetchStats()
    this.fetchDrivers()
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    async fetchTickets(params = {}) {
      this.loading = true
      try {
        const queryParams = {
          page: this.currentPage,
          page_size: this.pageSize,
          ...params,
        }
        if (this.search) queryParams.search = this.search
        if (this.statusFilter) queryParams.status = this.statusFilter
        if (this.dateRangeFilter) queryParams.date_range = this.dateRangeFilter
        if (this.driverId) queryParams.driver = this.driverId
        if (this.minContacts !== '' && this.minContacts != null) queryParams.min_contact_attempts = this.minContacts
        if (this.maxContacts !== '' && this.maxContacts != null) queryParams.max_contact_attempts = this.maxContacts
        if (this.activeTickets) queryParams.active_tickets = true
        if (this.canBeResolved) queryParams.can_be_resolved = true

        const { data } = await axios.get('/api/support/driver-tickets/', { params: queryParams })
        this.tickets = data.results || data
        this.pagination = {
          count: data.count || (Array.isArray(data) ? data.length : 0),
          next: data.next,
          previous: data.previous,
          page_size: data.page_size || this.pageSize,
          current_page: data.current_page || this.currentPage,
          total_pages: data.total_pages || 1,
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching driver tickets:', error)
        if (this.$bvToast) {
          this.$bvToast.toast('Erreur lors du chargement des tickets', { variant: 'danger' })
        }
      } finally {
        this.loading = false
      }
    },
    async fetchStats() {
      try {
        const { data } = await axios.get('/api/support/driver-tickets/dashboard-stats/')
        this.stats = data.all_time || data.driver_tickets || data
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error fetching stats', e)
      } finally {
        this.loading = false
      }
    },
    async patchTicket(id, payload) {
      this.actionLoadingId = id
      try {
        await axios.patch(`/api/support/driver-tickets/${id}/`, payload)
        if (this.$bvToast) this.$bvToast.toast('Ticket mis à jour', { variant: 'success' })
        await this.fetchTickets()
        await this.fetchStats()
        this.syncQuickViewTicket(id)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to patch ticket', e)
        if (this.$bvToast) this.$bvToast.toast('Échec de la mise à jour', { variant: 'danger' })
      } finally {
        this.actionLoadingId = null
      }
    },
    async addContactAttempt(id) {
      this.actionLoadingId = id
      try {
        await axios.post(`/api/support/driver-tickets/${id}/add-contact-attempt/`)
        if (this.$bvToast) this.$bvToast.toast('Tentative de contact ajoutée', { variant: 'success' })
        await this.fetchTickets()
        await this.fetchStats()
        this.syncQuickViewTicket(id)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to add contact attempt', e)
        if (this.$bvToast) this.$bvToast.toast("Échec de l'ajout", { variant: 'danger' })
      } finally {
        this.actionLoadingId = null
      }
    },
    async takeInProgress(item) {
      if (!this.canTransitionTo(item, 'in_progress')) return
      const ok = await this.confirmAction('Prendre en charge ce ticket ?')
      if (ok) this.patchTicket(item.id, { status: 'in_progress' })
    },
    async resolveTicket(item) {
      if (!this.canTransitionTo(item, 'resolved')) return
      const ok = await this.confirmAction('Marquer ce ticket comme résolu ?')
      if (ok) this.patchTicket(item.id, { status: 'resolved' })
    },
    async escalateTicket(item) {
      if (!this.canTransitionTo(item, 'escalated')) return
      const ok = await this.confirmAction('Escalader ce ticket ?')
      if (ok) this.patchTicket(item.id, { status: 'escalated' })
    },
    async closeTicket(item) {
      if (!['in_progress', 'resolved'].includes(item.status)) {
        if (this.$bvToast) this.$bvToast.toast('Le ticket doit être en cours ou résolu avant fermeture', { variant: 'warning' })
        return
      }
      const ok = await this.confirmAction('Fermer ce ticket ?')
      if (ok) this.patchTicket(item.id, { status: 'closed' })
    },
    confirmAction(message) {
      if (this.$bvModal && this.$bvModal.msgBoxConfirm) {
        return this.$bvModal.msgBoxConfirm(message, {
          title: 'Confirmation',
          okVariant: 'primary',
          okTitle: 'Confirmer',
          cancelTitle: 'Annuler',
          footerClass: 'p-2',
          hideHeaderClose: true,
          centered: true,
          size: 'sm',
        })
      }
      // If modal service not available, default to no
      if (this.$bvToast) this.$bvToast.toast('Action annulée (confirmation indisponible)', { variant: 'warning' })
      return Promise.resolve(false)
    },
    openQuickView(item) {
      this.quickViewTicket = item
      this.quickViewVisible = true
    },
    closeQuickView() {
      this.quickViewVisible = false
      this.quickViewTicket = null
    },
    onKeydown(e) {
      if (!this.lastHoverId) return
      const key = (e.key || '').toLowerCase()
      if (key === '.') {
        const item = this.tickets.find(t => t.id === this.lastHoverId)
        if (item) {
          this.openQuickView(item)
          e.preventDefault()
        }
      } else if (!this.canManageStatuses) {
        // user cannot manage statuses, do nothing
      } else if (key === 'a') {
        const item = this.tickets.find(t => t.id === this.lastHoverId)
        if (item && item.status !== 'in_progress') this.takeInProgress(item)
      } else if (key === 'r') {
        const item = this.tickets.find(t => t.id === this.lastHoverId)
        if (item) this.resolveTicket(item)
      } else if (key === 'e') {
        const item = this.tickets.find(t => t.id === this.lastHoverId)
        if (item) this.escalateTicket(item)
      } else if (key === 'f') { // f for close (fermer)
        const item = this.tickets.find(t => t.id === this.lastHoverId)
        if (item && item.status === 'resolved') this.closeTicket(item)
      } else if (key === 'c') {
        const item = this.tickets.find(t => t.id === this.lastHoverId)
        if (item) this.addContactAttempt(item.id)
      }
    },
    onPageChange(page) {
      this.currentPage = page
      this.applyFilters({ resetPage: false })
    },
    changePageSize() {
      this.currentPage = 1
      this.applyFilters({ resetPage: false })
    },
    getDisplayRange() {
      const start = (this.currentPage - 1) * this.pagination.page_size + 1
      const end = Math.min(this.currentPage * this.pagination.page_size, this.pagination.count)
      return `${start}-${end}`
    },
    applyFilters({ resetPage = true } = {}) {
      if (resetPage) this.currentPage = 1
      const params = {}
      if (this.statusFilter) params.status = this.statusFilter
      if (this.search) params.search = this.search
      if (this.dateRangeFilter) params.date_range = this.dateRangeFilter
      if (this.driverId) params.driver = this.driverId
      if (this.minContacts !== '' && this.minContacts != null) params.min_contact_attempts = this.minContacts
      if (this.maxContacts !== '' && this.maxContacts != null) params.max_contact_attempts = this.maxContacts
      if (this.activeTickets) params.active_tickets = true
      if (this.canBeResolved) params.can_be_resolved = true
      this.fetchTickets(params)
    },
    resetFilters() {
      this.statusFilter = ''
      this.search = ''
      this.dateRangeFilter = ''
      this.driverId = ''
      this.minContacts = ''
      this.maxContacts = ''
      this.activeTickets = false
      this.canBeResolved = false
      this.currentPage = 1
      this.applyFilters({ resetPage: false })
    },
    syncQuickViewTicket(ticketId = null) {
      if (!this.quickViewVisible) return
      const targetId = ticketId || (this.quickViewTicket && this.quickViewTicket.id)
      if (!targetId) return
      const updatedTicket = this.tickets.find(ticket => ticket.id === targetId)
      if (updatedTicket) {
        this.quickViewTicket = updatedTicket
      } else {
        this.closeQuickView()
      }
    },
    isRowBusy(id) {
      return String(this.actionLoadingId) === String(id)
    },
    canTransitionTo(item, nextStatus) {
      const transitionMap = {
        new: ['in_progress', 'resolved'],
        in_progress: ['resolved', 'closed'],
        resolved: ['closed'],
        escalated: ['resolved'],
        closed: [],
      }
      const currentStatus = (item.status || '').toString().toLowerCase()
      return (transitionMap[currentStatus] || []).includes(nextStatus)
    },
    hasAnyStatusShortcut(item) {
      return this.statusShortcutButtons.some(action => this.canTransitionTo(item, action.value))
    },
    async updateTicketStatus(item, nextStatus) {
      if (!this.canTransitionTo(item, nextStatus)) return
      const ok = await this.confirmAction(`Changer le statut du ticket #${item.id} vers "${this.getStatusLabel(nextStatus)}" ?`)
      if (!ok) return
      this.activeStatusChange = `${item.id}:${nextStatus}`
      await this.patchTicket(item.id, { status: nextStatus })
      this.activeStatusChange = null
    },
    // selection helpers
    toggleSelect(id) {
      if (this.selectedIds.includes(id)) this.selectedIds = this.selectedIds.filter(x => x !== id)
      else this.selectedIds = [...this.selectedIds, id]
    },
    isSelected(id) {
      return this.selectedIds.includes(id)
    },
    clearSelection() {
      this.selectedIds = []
    },
    async bulkPatch(ids, payload, confirmMsg, successMsg) {
      if (!this.canManageStatuses || !ids.length) return
      const ok = await this.confirmAction(`${confirmMsg} (${ids.length}) ?`)
      if (!ok) return
      for (let i = 0; i < ids.length; i += 1) {
        const id = ids[i]
        // sequential to keep UI predictable
        // eslint-disable-next-line no-await-in-loop
        await this.patchTicket(id, payload)
      }
      if (this.$bvToast) this.$bvToast.toast(successMsg, { variant: 'success' })
      this.clearSelection()
    },
    bulkResolve() { this.bulkPatch(this.selectedIds, { status: 'resolved' }, 'Marquer comme résolus', 'Tickets résolus') },
    bulkEscalate() { this.bulkPatch(this.selectedIds, { status: 'escalated' }, 'Escalader', 'Tickets escaladés') },
    bulkClose() {
      this.bulkPatch(this.selectedIds.filter(id => {
        const t = this.tickets.find(x => x.id === id)
        return t && ['in_progress', 'resolved'].includes(t.status)
      }), { status: 'closed' }, 'Fermer (tickets en cours ou résolus)', 'Tickets fermés')
    },
    getStatusClass(status) {
      if (!status) return 'status-open'
      const s = (status || '').toString().toLowerCase()
      if (s === 'new') return 'status-open'
      if (s === 'in_progress') return 'status-progress'
      if (s === 'resolved') return 'status-resolved'
      if (s === 'closed') return 'status-closed'
      if (s === 'escalated') return 'status-escalated'
      return 'status-open'
    },
    getStatusIcon(status) {
      if (!status) return 'fas fa-clock'
      const s = (status || '').toString().toLowerCase()
      if (s === 'new') return 'fas fa-bell'
      if (s === 'in_progress') return 'fas fa-spinner'
      if (s === 'resolved') return 'fas fa-check'
      if (s === 'closed') return 'fas fa-times'
      if (s === 'escalated') return 'fas fa-exclamation-triangle'
      return 'fas fa-clock'
    },
    getStatusLabel(status) {
      if (!status) return '-'
      const s = (status || '').toString().toLowerCase()
      if (s === 'new') return 'Nouveau'
      if (s === 'in_progress') return 'En cours'
      if (s === 'resolved') return 'Résolu'
      if (s === 'closed') return 'Fermé'
      if (s === 'escalated') return 'Escaladé'
      return status
    },
    formatReason(reason) {
      if (!reason) return '-'
      // reason may be object or string
      if (typeof reason === 'object') return reason.content || reason.label || reason.name || reason.title || reason.slug || '-'
      return reason
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString('fr-FR', {
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    },
    goToDetail(item) {
      this.$router.push({ name: 'DriverTicketDetail', params: { id: item.id } })
    },
  },
}
</script>

<style lang="scss" scoped>
.modern-container {
  padding: 1.5rem;
  animation: slideUp 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.tickets-header {
  background: linear-gradient(135deg, var(--brand-primary-dark, #031729) 0%, var(--brand-primary, #052240) 50%, var(--brand-primary-light, #1A5276) 100%);
  padding: 2.5rem 2rem;
  border-radius: 16px;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(var(--brand-primary-rgb, 5, 34, 64), 0.25);
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: url('data:image/svg+xml;utf8,<svg width="20" height="20" xmlns="http://www.w3.org/2000/svg"><circle cx="2" cy="2" r="2" fill="rgba(255,255,255,0.05)"/></svg>');
    opacity: 0.5;
  }
}

.simple-title {
  font-weight: 700;
  margin: 0;
  color: white;
  position: relative;
  z-index: 1;
  font-size: 1.8rem;
  letter-spacing: 0.5px;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.stats-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 2rem;

  .stat-item {
    flex: 1;
    min-width: 200px;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    padding: 1.5rem;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
    align-items: center;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    border: 1px solid rgba(0, 0, 0, 0.03);
    position: relative;
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 4px;
      background: linear-gradient(90deg, #ff0000, #ff4d4d);
      transform: scaleX(0);
      transition: transform 0.3s ease;
      transform-origin: left;
    }

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 12px 40px rgba(255, 0, 0, 0.12);

      &::after {
        transform: scaleX(1);
      }
    }

    .stat-icon-wrapper {
      margin-bottom: 0.75rem;
      padding: 0.75rem;
      border-radius: 50%;
      background: rgba(0,0,0,0.03);
    }

    .stat-number {
      font-size: 2.2rem;
      font-weight: 800;
      color: #090909;
      margin-bottom: 0.2rem;
      line-height: 1;
    }
    .stat-label {
      color: #6e6b7b;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 1px;
      font-size: 0.85rem;
    }
  }
}

.modern-card {
  border-radius: 16px;
  border: none;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
  background: white;
  overflow: hidden;
}

.filter-bar {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid rgba(0,0,0,0.03);
}

.filter-select, .filter-input {
  border: 1px solid #e0e0e0 !important;
  border-radius: 8px !important;
  background-color: white;
  padding: 0.5rem 1rem;
  transition: all 0.2s;
  &:focus {
    border-color: #ff0000 !important;
    box-shadow: 0 0 0 0.2rem rgba(255, 0, 0, 0.25) !important;
    outline: none;
  }
}

::v-deep .table-hover tbody tr {
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(255, 0, 0, 0.03) !important;
    transform: scale(1.005);
    box-shadow: 0 4px 15px rgba(0,0,0,0.05);
    border-radius: 8px;
  }
}

/* Pagination Styles */
.pagination-container {
  border-top: 1px solid #f1f5f9;
  padding-top: 1rem;
  background: #f8f9fa;
  border-radius: 0 0 20px 20px;
}

.pagination-info {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

::v-deep .pagination .page-link {
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  color: #64748b;
  font-weight: 500;
  margin: 0 2px;
}

::v-deep .pagination .page-item.active .page-link {
  background: linear-gradient(135deg, #ff0000 0%, #ff4d4d 100%);
  border-color: #ff0000;
  color: white;
}

::v-deep .pagination .page-link:hover {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
  color: #1e293b;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  border: 0;
}

/* Specific Cell Styles */
.status-pill {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}
.status-open {
  background: linear-gradient(135deg, #090909 0%, #1a202c 100%);
  color: white;
}
.status-progress {
  background: rgba(91, 166, 227, 0.68);
  color: white;
}
.status-resolved {
  background: #2FDD6F;
  color: white;
}
.status-closed {
  background: linear-gradient(135deg, #ff0000 0%, #ff0000 100%);
  color: white;
}
.status-escalated {
  background: linear-gradient(135deg, #ff9f43 0%, #ff8c00 100%);
  color: white;
}

.order-badge {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #000000;
  padding: 0.5rem 1rem;
  border-radius: 25px;
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}
.order-badge:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(255, 0, 0, 0.15);
  background: linear-gradient(135deg, #ff0000 0%, #ff4d4d 100%);
  color: white;
}

.user-avatar-alt {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff0000 0%, #ff4d4d 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
}
.order-cell, .user-cell, .actions-cell {
  display: flex;
  align-items: center;
}
.actions-cell {
  gap: 0.5rem;
}
.date-cell {
  color: #64748b;
  font-size: 0.875rem;
}
.user-name, .order-info {
  font-weight: 500;
  color: #1e293b;
}
.icon-btn { width: 34px; height: 34px; display: inline-flex; align-items: center; justify-content: center; border-radius: 8px; }
.bulk-toolbar { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 10px; box-shadow: 0 6px 20px rgba(0,0,0,0.06); }
.status-actions { display: flex; flex-wrap: wrap; gap: 0.45rem; margin-top: 0.75rem; }
.status-shortcut-btn { min-width: 92px; font-weight: 600; }

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
  color: #64748b;
}

/* Responsive */
@media (max-width: 768px) {
  .modern-container {
    padding: 1rem;
  }
  .header-content {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  .status-actions {
    flex-direction: column;
  }
}
</style>
